import React, { useCallback } from "react";
import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { Renderify } from "renderify-js";
import SwitchField from "./SwitchField";
import { VisibilityCondition } from "./VisibilityCondition";
import type { Variable } from "./VisibilityCondition/types";

interface ConditionData {
  [key: string]: unknown;
}

interface VisibilitySectionProps {
  // Props para controlar la visibilidad de la sección
  shouldShow: boolean;
  variableList: Variable[];

  // Props para el estado condicional
  isConditional: boolean;
  onConditionalChange: (value: boolean) => void;

  // Props para las condiciones
  conditions: Array<{ id: number | string; data: ConditionData }>;
  onConditionsChange: (
    conditions: Array<{ id: number | string; data: ConditionData }>,
  ) => void;

  // Props para el operador del campo
  operator: "OR" | "AND";
  onOperatorChange: (operator: "OR" | "AND") => void;

  // Props para validación
  onValidityChange: (id: string) => (isValid: boolean) => void;
}

function shallowEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;
  if (obj1 === null || obj2 === null) return false;

  const keys1 = Object.keys(obj1 as Record<string, unknown>);
  const keys2 = Object.keys(obj2 as Record<string, unknown>);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (
      (obj1 as Record<string, unknown>)[key] !==
      (obj2 as Record<string, unknown>)[key]
    )
      return false;
  }

  return true;
}

const VisibilitySection: React.FC<VisibilitySectionProps> = ({
  shouldShow,
  variableList,
  isConditional,
  onConditionalChange,
  conditions,
  onConditionsChange,
  operator,
  onOperatorChange,
  onValidityChange,
}) => {
  const handleAdd = () => {
    onConditionsChange([...conditions, { id: Date.now(), data: {} }]);
  };

  const handleToggleOperator = () => {
    onOperatorChange(operator === "OR" ? "AND" : "OR");
  };

  const handleRemove = (id: number | string) => {
    onConditionsChange(conditions.filter((c) => c.id !== id));
  };

  const handleChangeStable = useCallback(
    (id: number | string, newData: ConditionData) => {
      onConditionsChange(
        conditions.map((c) =>
          c.id === id
            ? shallowEqual(c.data, newData)
              ? c
              : { ...c, data: newData }
            : c,
        ),
      );
    },
    [conditions, onConditionsChange],
  );

  return (
    <Renderify condition={shouldShow} elseShow={<br />}>
      <>
        <hr className="border-neutral-quiet my-6" />

        <Heading variant="h6" className="mb-2">
          Visibilidad
        </Heading>

        <SwitchField
          label="¿Es condicional?"
          isTrue="Visible cuando:"
          isFalse="Siempre visible"
          checked={isConditional}
          onChange={() => onConditionalChange(!isConditional)}
          required
        />
        {isConditional && (
          <div className="space-y-4">
            {conditions.map((c, index) => (
              <React.Fragment key={c.id}>
                {index > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="quiet"
                      size="xs"
                      onClick={handleToggleOperator}
                      className="font-semibold text-xs"
                    >
                      {operator}
                    </Button>
                    <hr className="w-full border-neutral-quiet" />
                  </div>
                )}
                <div className="flex gap-1 items-center">
                  <VisibilityCondition
                    initialData={c.data as ConditionData}
                    onChange={(newData) =>
                      handleChangeStable(c.id, newData as ConditionData)
                    }
                    variableList={variableList}
                    onValidityChange={onValidityChange(String(c.id))}
                  />
                  {index > 0 && (
                    <Button
                      className="mt-4"
                      variant="ghost"
                      onClick={() => handleRemove(c.id)}
                    >
                      <Icon type="far" name="xmark" />
                    </Button>
                  )}
                </div>
              </React.Fragment>
            ))}
            <Button variant="quiet" onClick={handleAdd}>
              Añadir condición
              <Icon type="far" name="plus" />
            </Button>
          </div>
        )}
      </>
    </Renderify>
  );
};

export default VisibilitySection;
